import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import { UPDATE_LEAD_PREFERENCES } from 'apollo/leads.query';
import { fetchOrgMembers } from 'apollo/organization.query';
import ViewOptions, {
  ViewOptionsHeading,
  ViewOptionsCheckbox,
  ViewOptionsOption,
  ViewOptionsSelect,
} from 'components/ViewOptions';
import { MobileSm } from 'components/MediaQueries';
import { SET_LEAD_VIEW_OPTIONS } from 'containers/App/constants';
import { LEADS_VIEW_OPTIONS_GROUPS } from 'utils/constants';
import { SORTABLE_COLUMNS } from './Leads.Table';

const FIXED_COLUMN_IDS = ['sortable-group-1', 'contactName', 'contactEmail', 'contactNumber', 'contactCompanyName'];

const SortableItem = (props) => {
  const { column } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    active,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString({ ...transform, scaleX: 1, scaleY: 1 }),
    transition,
    height: 25,
    background: isDragging ? '#f6f6f6' : undefined,
    touchAction: 'none',
    pointerEvents: active ? 'none' : undefined,
  };
  return (
    <div
      {...attributes}
      {...listeners}
      ref={setNodeRef}
      className={clsx(isDragging && 'vertical-dnd-indicator')}
      style={style}
    >
      <OptionRow
        {...props}
      />
    </div>
  );
};

const SortableGroup = ({ group, onChangeCheck }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    active,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString({ ...transform, scaleX: 1, scaleY: 1 }),
    transition,
    background: isDragging ? '#f6f6f6' : undefined,
    touchAction: 'none',
    pointerEvents: active ? 'none' : undefined,
  };

  return (
    <div
      {...attributes}
      {...listeners}
      ref={setNodeRef}
      className={clsx(isDragging && 'vertical-dnd-indicator')}
      style={style}
    >
      <div className="view-option--group">
        {group.children.map(item => {
          return (
            <OptionRow
              key={`lead-view-option__${item.id}`}
              column={item}
              onChangeCheck={() => onChangeCheck(item.id)}
            />
          );
        })}
      </div>
    </div>
  );
};

export const OptionRow = ({ column, onChangeCheck, draggable = true }) => {
  const inputProps = {
    checked: column.checked,
    onChange: onChangeCheck,
  };

  return (
    <ViewOptionsCheckbox
      id={column.id}
      inputProps={inputProps}
      style={{ cursor: draggable ? 'grab' : 'default' }}
    >
      {column.name}
    </ViewOptionsCheckbox>
  );
};

export const groupOptions = (options, groupings) => {
  const alreadyGrouped = [];
  const groupedOptions = [];

  for (let optIdx = 0; optIdx < options.length; optIdx+=1) {
    const option = options[optIdx];

    for (let groupIdx = 0; groupIdx < groupings.length; groupIdx+=1) {
      const grouping = groupings[groupIdx];

      if (grouping.includes(option.id)) {
        if (!alreadyGrouped.includes(option.id)) {
          const groupedOpts = options.filter(opt => grouping.includes(opt.id));

          alreadyGrouped.push(...groupedOpts.map(opt => opt.id));

          groupedOptions.push({
            id: `sortable-group-${groupIdx}`,
            children: groupedOpts,
          });
        }
      }
    }

    if (!alreadyGrouped.includes(option.id)) {
      groupedOptions.push(option);
    }
  }

  return groupedOptions;
};

/**
 * @description: ViewOption Component for Leads Table
 * @param {*}
 * @return {*}
 */
export const ViewOption = ({
  options = [],
  selected = 'current',
  sortColumn,
  changeSortColumn,
  filterAssignment,
  changeFilterAssignment,
}) => {
  const dispatch = useDispatch();
  const filters = useSelector(state => state.leads.filters);
  const userInfo = useSelector((state) => state.global.userInfo);
  const { organization, permissions } = userInfo;
  const { id: orgId } = organization;

  const [orgMembers, setOrgMembers] = useState([]);
  const [updateLoginLeadsPreferences] = useMutation(UPDATE_LEAD_PREFERENCES);
  const [leadOptions, setLeadOptions] = useState(
    groupOptions(options, LEADS_VIEW_OPTIONS_GROUPS.map(group => group.grouping)),
  );
  const [filterOption, setFilterOption] = useState(filterAssignment?.length > 0 ? 'filterBy' : 'all');
  const statusName = filters?.filter(fl => String(fl.id) === String(selected))[0]?.name;
  const companyName = useMemo(() => {
    let _companyName = null;
    leadOptions.forEach(option => {
      if (option.children) {
        const foundChild = option.children.find(child => child.id === 'contactCompanyName');
        if (foundChild) _companyName = foundChild;
      }
      if (option.id === 'contactCompanyName') {
        _companyName = option;
      }
    });
    return _companyName;
  }, [leadOptions]);

  const handleDragEnd = (event) => {
     const { active, over } = event;
    setLeadOptions(prev => {
      const newIndex = leadOptions.findIndex(o => o.id === over.id);
      const oldIndex = leadOptions.findIndex(o => o.id === active.id);

      return arrayMove(prev, oldIndex, newIndex);
    });

  };

  useEffect(() => {
    const updatePreferences = async (preferences) => {
      await updateLoginLeadsPreferences({
        variables: {
          input: JSON.stringify(preferences),
        },
      });
    };

    if (leadOptions) {
      const flattenedLeadOptions = flatten(leadOptions);
      updatePreferences(flattenedLeadOptions);
      dispatch({
        type: SET_LEAD_VIEW_OPTIONS,
        payload: flattenedLeadOptions,
      });
    }
  }, [leadOptions]);

  useEffect(() => {
    setFilterOption(filterAssignment?.length > 0 ? 'filterBy' : 'all');
  }, [filterAssignment]);

  useEffect(() => {
    async function fetch() {
      const { data } = await fetchOrgMembers({ orgId });
      setOrgMembers(data.organizationMembers);
    }
    fetch();
  }, []);

  const onChangeCheck = useCallback(
    (id) => {
      const newColumns = flatten(leadOptions).map(column => {
        if (column.id === id) {
          return { ...column, checked: !column.checked };
        }
        return column;
      });
      setLeadOptions(groupOptions(newColumns, LEADS_VIEW_OPTIONS_GROUPS.map(group => group.grouping)));
    },
  );

  const onChangeFilterOption = (e) => {
    if (e.target.value === 'all') {
      setFilterOption(e.target.value);
      changeFilterAssignment(null);
    }
    else if (e.target.value === 'filterBy') {
      setFilterOption(e.target.value);
    }
  };

  const onChangeFilterAssignment = (e) => {
    if (e.target?.value && e.target?.value !== '0') {
      changeFilterAssignment([parseInt(e.target.value, 10)]);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const renderSortableContent = () => {
    return (
      <>
        <ViewOptionsHeading>Visible Fields</ViewOptionsHeading>
        <ViewOptionsCheckbox
          id="contactName"
          style={{ opacity: 0.4 }}
          className="disabled"
          inputProps={{ name: 'contactName', checked: true, disabled: true }}
        >
          Lead Name
        </ViewOptionsCheckbox>
        {!!companyName && (
          <OptionRow
            column={companyName}
            onChangeCheck={() => onChangeCheck(companyName.id)}
            draggable={false}
          />
        )}
        <ViewOptionsCheckbox
          id="contactEmail"
          style={{ opacity: 0.4 }}
          className="disabled"
          inputProps={{ name: 'contactEmail', checked: true, disabled: true }}
        >
          Email
        </ViewOptionsCheckbox>
        <ViewOptionsCheckbox
          id="contactNumber"
          style={{ opacity: 0.4 }}
          className="disabled"
          inputProps={{ name: 'contactNumber', checked: true, disabled: true }}
        >
          Phone #
        </ViewOptionsCheckbox>
        <DndContext
          sensors={sensors}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
          collisionDetection={closestCenter}
        >
          <SortableContext
            items={leadOptions}
            strategy={verticalListSortingStrategy}
          >
            {leadOptions
              .filter(fl => {
                if (fl.id === 'lostReasonId' && statusName !== 'Lost') return false;
                return !FIXED_COLUMN_IDS.includes(fl.id);
              })
              .map((column, idx) => {
                if (column.children) {
                  return (
                    <SortableGroup
                      key={`lead-view-option__group-${idx}`}
                      group={column}
                      index={idx}
                      onChangeCheck={onChangeCheck}
                    />
                  );
                }

                return (
                  <SortableItem
                    key={`lead-view-option__${column.id}`}
                    column={column}
                    index={idx}
                    onChangeCheck={() => onChangeCheck(column.id)}
                  />
                );
            })}
          </SortableContext>
        </DndContext>
      </>
    );
  };

  const sortLeadsByStatus = (column) => {
    changeSortColumn({
      name: column.label,
      sortReverse: column.label === 'Inquiry Date' ? 0 : 1,
    });
  };

  const renderSortBySection = () => (
    SORTABLE_COLUMNS?.map((column, idx) => {
      const id = `sort-by-option-${column.id}`;
      const inputProps = {
        name: 'sortBy',
        checked: sortColumn.name === column.label,
        onChange: () => sortLeadsByStatus(column),
      };

      return (
        <ViewOptionsOption
          key={`sort_option_${idx}`}
          id={id}
          inputProps={inputProps}
        >
          {column.label}
        </ViewOptionsOption>
      );
    })
  );

  const renderFilterByAssignment = () => {
    const inputProps = {
      name: 'filterBy',
      onChange: onChangeFilterOption,
    };
    return (
      <>
        <ViewOptionsHeading>Filter By</ViewOptionsHeading>
        <ViewOptionsOption
          id="filter-by--all"
          inputProps={{
            value: 'all',
            checked: filterOption === 'all',
            ...inputProps,
          }}
        >
          Show All
        </ViewOptionsOption>
        <ViewOptionsOption
          id="filter-by--assignment"
          inputProps={{
            value: 'filterBy',
            checked: filterOption === 'filterBy',
            ...inputProps,
          }}
        >
          Lead Assignment
        </ViewOptionsOption>
        {filterOption === 'filterBy' && (
          <ViewOptionsSelect
            inputProps={{
              onChange: onChangeFilterAssignment,
              value: filterAssignment?.length > 0 ? filterAssignment[0] : 0,
            }}
          >
            {orgMembers.map(member => {
              return (
                <option
                  key={`filter-by--${member.loginId}`}
                  value={member.loginId}
                >
                  {member.firstname} {member.lastname}
                </option>
              );
            })}
          </ViewOptionsSelect>
        )}
      </>
    );
  };

  return (
    <ViewOptions
      adjustOnVerticalOverflow={false}
      scrollLockOnOpen={false}
    >
      <MobileSm>
        <div className="view-options__section">
          <ViewOptionsHeading>Sort By</ViewOptionsHeading>
          {renderSortBySection()}
        </div>
      </MobileSm>
      {permissions && permissions['lead.all'] && (
        <div className="view-options__section">
          {renderFilterByAssignment()}
        </div>
      )}
      <div className="view-options__section">
        { renderSortableContent() }
      </div>
    </ViewOptions>
  );
};

// Flatten grouped items from an object with children.
const flatten = (items) => {
  let flattenedItems = [];
  items.forEach(item => {
    if (item.children) {
      item.children.forEach(i => {
        flattenedItems = [...flattenedItems, i];
      });
    }
    else {
      flattenedItems = [...flattenedItems, item];
    }
  });

  return flattenedItems;
};
